import * as React from "react";

import { debounce, camelCase } from "lodash";
import { Box, Button, FormField, MaskedInput, Select, TextArea, TextInput, Text } from "grommet";
import { normalizeColor } from "grommet/utils";
import { QuestionQueryModel, getSubmissionData, Option, GetReferenceList, Question } from "../utils/ServiceManagerAPI";
import Theme from "../utils/Theme";
import { useDarkMode } from "./DarkModeSwitch";
import SignatureOption from "./SignatureOption";
import { QuestionnaireContext } from "../routes/Questions";
import { validate as validateFunction } from "../utils/ServiceManagerAPI";

const { Component } = React;
const AnswerableOption = ({
    question,
    option,
    changed,
    value: propValue,
    atomic,
    disabled,
    style,
    onValidate,
    validate,
}: {
    question: Question;
    option: Option;
    changed: Function;
    value: any;
    atomic: boolean;
    disabled: boolean;
    style: Object;
    onValidate: Function;
    validate: boolean;
}) => {
    const [value, setValue] = React.useState("" as any);
    const [valid, setValid] = React.useState(question.dataValidations?.length == 0);

    const [referenceList, setReferenceList] = React.useState([] as any[]);
    const [referenceValue, setReferenceValue] = React.useState({} as any);

    React.useEffect(() => {
        if (option.referenceListID) {
            GetReferenceList(option.referenceListID).then((res) => {
                setReferenceList(res.data.result.items);
                setReferenceValue(res.data.result.items.find((item) => item.name == value));
            });
        }
    }, [value]);

    // React.useEffect(() => {
    //     setValid(doValidation(value));
    // }, [value]);

    const questionnaire = React.useContext(QuestionnaireContext);

    const doChange = (v) => {
        doFocus(false);
        if (!changed(v)) {
            setValue(propValue);
        }
    };

    React.useEffect(() => {
        setValid(doValidation(propValue));
        if (value != propValue) {
            let v = propValue;

            if (v === null) {
                v = "";
            }

            switch (v?.toString()?.toLowerCase()) {
                case "true":
                    v = true;
                    break;
                case "false":
                    v = false;
                    break;
                case null:
                    v = "";
                    break;
            }

            setValue(v);
        }
    }, [propValue]);

    const doValidation = (v) => {
        if (validate) {
            let valid = true;
            question.dataValidations?.forEach((validation) => {
                if (!validation.optionsAppliedTo || validation.optionsAppliedTo.length == 0 || validation.optionsAppliedTo.includes(option.id)) {
                    valid = valid && validateFunction(validation, v, question, getSubmissionData(questionnaire));
                }
            });
            onValidate && onValidate(valid, option.id);
            return valid;
        }
        return true;
    };

    const handleChange = (e) => {
        let v = e.target.value;
        if (option.type == 0) {
            v = !value;
        }

        setValue(v);
        setValid(doValidation(v));
        return v;
    };

    const handleAtomicChange = (value) => (e) => {
        setValue(value);
        doChange(value);
        setValid(doValidation(value));
    };

    const handleChangeAndUpdate = (e) => {
        let value = handleChange(e);
        doChange(value);
        setValid(doValidation(value));
    };

    const handleReferenceChange = (value) => {
        setReferenceValue(value);
        doChange(value.name);
        setValid(doValidation(value));
    };

    const [isDarkMode] = useDarkMode();

    const inputStyle = {
        backgroundColor: normalizeColor("background", Theme, isDarkMode),
    };

    const buttonStyle = {
        // color: `var(${isDarkMode ? "--data-color-dark" : "--data-color-light"},${isDarkMode ? "var(--text-color-dark)" : "var(--text-color-light)"})`,
    };

    const [focus, setFocus] = React.useState(false);

    const doFocus = (focus) => {
        setFocus(focus);
    };

    React.useEffect(() => {
        if (focus) {
            window.onbeforeunload = function (evt) {
                if (propValue != value) {
                    changed(value);
                }
            };
        } else {
            window.onbeforeunload = null;
        }
    }, [focus, value, propValue]);

    switch (option.type) {
        case 0: {
            return (
                <>
                    {atomic && (
                        <Box gap="xsmall" style={style}>
                            {option.text && <Text>{option.text}</Text>}
                            <Box gap="xsmall" direction="row">
                                <Button
                                    label="Yes"
                                    color={!disabled ? "brand" : "background-front"}
                                    style={buttonStyle}
                                    disabled={disabled}
                                    primary={value == true}
                                    onClick={handleAtomicChange(true)}
                                ></Button>
                                <Button
                                    label="No"
                                    color={!disabled ? "brand" : "background-front"}
                                    style={buttonStyle}
                                    disabled={disabled}
                                    primary={value == false}
                                    onClick={handleAtomicChange(false)}
                                ></Button>
                            </Box>
                        </Box>
                    )}
                    {!atomic && (
                        <Button
                            style={{ marginBottom: "0.3rem", ...buttonStyle, ...style, backgroundColor: !!value ? "" : normalizeColor("background", Theme, isDarkMode) }}
                            label={option.text}
                            disabled={disabled}
                            primary={!!value}
                            color={!disabled ? "brand" : "background-front"}
                            onClick={handleChangeAndUpdate}
                        ></Button>
                    )}
                </>
            );
        }
        case 1:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={{ ...style }} disabled={disabled}>
                        <TextInput
                            onFocus={() => doFocus(true)}
                            style={{ ...buttonStyle, ...inputStyle, width: "100%", minHeight: "44.67px", maxHeight: "150px", height: "34px" }}
                            autoComplete={"nope"}
                            name={camelCase(option.text)}
                            value={value}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            disabled={disabled}
                        />
                    </FormField>
                </Box>
            );
        case 2:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={style} disabled={disabled}>
                        <TextInput
                            onFocus={() => doFocus(true)}
                            style={{ ...buttonStyle, ...inputStyle }}
                            type="number"
                            autoComplete={"nope"}
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                        />
                    </FormField>
                </Box>
            );
        case 4:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={style} disabled={disabled}>
                        <TextInput
                            onFocus={() => doFocus(true)}
                            style={{ ...buttonStyle, ...inputStyle }}
                            type="date"
                            autoComplete={"nope"}
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                        />
                    </FormField>
                </Box>
            );
        case 5:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={style} disabled={disabled}>
                        <TextInput
                            onFocus={() => doFocus(true)}
                            style={{ ...buttonStyle, ...inputStyle }}
                            type="time"
                            autoComplete={"nope"}
                            name={camelCase(option.text)}
                            value={value}
                            disabled={disabled}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                        />
                    </FormField>
                </Box>
            );
        case 6:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={style} disabled={disabled}>
                        {!referenceList && <></>}
                        {referenceList && (
                            <Select
                                style={{ ...buttonStyle, ...inputStyle }}
                                disabled={disabled}
                                options={referenceList}
                                autoComplete={"nope"}
                                name={camelCase(option.text)}
                                value={referenceValue}
                                valueKey="name"
                                labelKey="name"
                                onChange={({ value }) => handleReferenceChange(value)}
                            />
                        )}
                    </FormField>
                </Box>
            );
        case 7:
            return (
                <Box>
                    <FormField label={option.text && option.text} style={{ ...style }} disabled={disabled}>
                        <TextArea
                            onFocus={() => doFocus(true)}
                            style={{ ...buttonStyle, ...inputStyle, minHeight: "150px", maxHeight: "150px" }}
                            autoComplete={"nope"}
                            name={camelCase(option.text)}
                            value={value}
                            onChange={handleChange}
                            onBlur={() => doChange(value)}
                            disabled={disabled}
                            size={"small"}
                        />
                    </FormField>
                </Box>
            );
        case 8:
            return <SignatureOption changed={doChange} value={value} disabled={disabled}></SignatureOption>;
    }

    return <></>;
};
export default AnswerableOption;
